import { createContext, useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { getJwtData } from "../util/JwtUtil";
import LoadingOverlay from "../components/LoadingOverlay";
import { tailChase } from "ldrs";

tailChase.register();

// ✅ Create Context
export const ConfigContext = createContext(null);

// 🔗 External references for use outside React
let externalSetJwt = null;
let externalSetRawJwt = null;
let externalSetVersionInfo = null;
let externalSetReadySent = null; // ✅ NEW
let externalNavigate = null;

// 🔗 External value mirrors
let externalJwtValue = null;
let externalRawJwtValue = null;
let externalReadySentValue = null; // ✅ NEW

// ✅ Function to clear context globally (usable from utils)
export const clearJwtContext = () => {
  if (externalSetJwt && externalSetRawJwt) {
    externalSetJwt(null);
    externalSetRawJwt(null);
    if (externalSetVersionInfo) externalSetVersionInfo(null);
    Cookies.remove("token");

    externalJwtValue = null;
    externalRawJwtValue = null;
    externalReadySentValue = null; // ✅ Reset

    console.log("✅ JWT context cleared globally");
  } else {
    console.warn("⚠️ ConfigProvider not mounted yet!");
  }
};

// ✅ Functions to read values globally
export const getCurrentJwt = () => externalJwtValue;
export const getCurrentRawJwt = () => externalRawJwtValue;
export const getReadySent = () => externalReadySentValue; // ✅ NEW

// Optional global setter
export const setReadySentGlobal = (val) => {
  if (externalSetReadySent) externalSetReadySent(val);
};

// ✅ Global navigation outside React
export const globalNavigate = (path, options = {}) => {
  if (externalNavigate) {
    externalNavigate(path, options);
  } else {
    console.warn(
      "⚠️ Navigation not available yet — ConfigProvider not mounted. Falling back to window.location.href"
    );
    window.location.href = path;
  }
};

export const ConfigProvider = ({ children }) => {
  const [jwt, setJwt] = useState(null);
  const [rawJwt, setRawJwt] = useState(null);
  const [versionInfo, setVersionInfo] = useState(null);
  const [readySent, setReadySent] = useState(false); // ✅ NEW
  const [jwtLoading, setJwtLoading] = useState(true);
  const dialogRef = useRef(null);
  const navigate = useNavigate();

  // 🔗 Register setter functions globally
  useEffect(() => {
    externalSetJwt = setJwt;
    externalSetRawJwt = setRawJwt;
    externalSetVersionInfo = setVersionInfo;
    externalSetReadySent = setReadySent; // ✅ NEW
    externalNavigate = navigate;

    return () => {
      externalSetJwt = null;
      externalSetRawJwt = null;
      externalSetVersionInfo = null;
      externalSetReadySent = null; // ✅ NEW
      externalNavigate = null;
    };
  }, [navigate]);

  // 🔁 Keep global mirrors in sync
  useEffect(() => {
    externalJwtValue = jwt;
    externalRawJwtValue = rawJwt;
    externalReadySentValue = readySent; // ✅ NEW
  }, [jwt, rawJwt, readySent]);

  // ✅ Load token from cookies on mount
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      try {
        const jwtData = getJwtData(token);
        setRawJwt(token);
        setJwt(jwtData || null);
      } catch (error) {
        console.error("❌ Failed to decode JWT:", error);
        setRawJwt(null);
        setJwt(null);
      }
    } else {
      console.warn("⚠️ No token found in cookies");
    }

    setJwtLoading(false);
  }, []);

  // ✅ Memoized context value
  const contextValue = useMemo(
    () => ({
      jwt,
      versionInfo,
      setVersionInfo,
      rawJwt,
      setJwt,
      setRawJwt,
      readySent, // ✅ INCLUDED
      setReadySent, // ✅ INCLUDED
      dialogRef,
    }),
    [jwt, rawJwt, versionInfo, readySent]
  );

  // Loader while initializing JWT
  if (jwtLoading) {
    return <LoadingOverlay type={1} />;
  }

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};
